import * as React from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertBanner, Button, DataTable, Modal, Tag, useLocale, useToast, type DataTableColumn } from '@ministry/ui/internal';

import { getUser, getUserSessions, revokeAllUserSessions, revokeSession, type AdminSession } from '../../lib/admin-users.js';
import { ApiError, NetworkError } from '../../lib/api-client.js';
import { formatInstantDisplay } from '../../lib/dates.js';
import { useT } from '../../i18n/strings.js';
import { PageHeader } from '../../components/PageHeader.js';
import { ForbiddenState } from '../../components/ForbiddenState.js';
import { ADMIN_ROUTES } from './routes.js';

/**
 * SCREEN_SPECS.md I22 — one user's active sessions: device/IP/last-seen, revoke one,
 * revoke all. `GET /users/{id}/sessions` (`session.read` — HR, AUD read-only) and
 * `DELETE /sessions/{id}` / `POST /users/{id}/sessions/revoke-all` (`session.revoke` —
 * HR/SA only, **AUD denied**, AC-36) are two different permissions, so an Auditor sees
 * this page's table with no action column at all rather than disabled buttons.
 */
export function UserSessionsPage(): React.JSX.Element {
  const { userId } = useParams<{ userId: string }>();
  const t = useT();
  const { locale } = useLocale();
  const { push } = useToast();
  const queryClient = useQueryClient();
  const [revokeAllOpen, setRevokeAllOpen] = React.useState(false);
  const [revokeOneId, setRevokeOneId] = React.useState<string | null>(null);

  const userQuery = useQuery({
    queryKey: ['admin-user', userId],
    queryFn: ({ signal }) => getUser(userId as string, signal),
    enabled: Boolean(userId),
  });

  const sessionsQuery = useQuery({
    queryKey: ['admin-user-sessions', userId],
    queryFn: ({ signal }) => getUserSessions(userId as string, signal),
    enabled: Boolean(userId),
  });

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['admin-user-sessions', userId] });

  const revokeOneMutation = useMutation({
    mutationFn: (sessionId: string) => revokeSession(sessionId),
    onSuccess: () => {
      setRevokeOneId(null);
      invalidate();
      push({ variant: 'success', title: t('admin.sessions.toast.revokedOne') });
    },
    onError: () => {
      push({ variant: 'error', title: t('admin.sessions.error.stillActiveOne') });
    },
  });

  const revokeAllMutation = useMutation({
    mutationFn: (reason: string) => revokeAllUserSessions(userId as string, reason),
    onSuccess: (result) => {
      setRevokeAllOpen(false);
      invalidate();
      push({ variant: 'success', title: t('admin.sessions.toast.revokedAll', { count: result.sessionsRemoved }) });
    },
    onError: () => {
      push({ variant: 'error', title: t('admin.sessions.error.stillActiveAll') });
    },
  });

  if (userQuery.isError && userQuery.error instanceof ApiError && (userQuery.error.code === 'NOT_FOUND' || userQuery.error.code.startsWith('FORBIDDEN'))) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader title={t('admin.sessions.title')} breadcrumb={[{ label: t('admin.users.title'), href: ADMIN_ROUTES.users }]} />
        <ForbiddenState error={userQuery.error} backTo={ADMIN_ROUTES.users} />
      </div>
    );
  }

  const userName = userQuery.data?.fullName ?? '';
  const rows = sessionsQuery.data ?? [];
  const canRevoke = !(sessionsQuery.isError && sessionsQuery.error instanceof ApiError && sessionsQuery.error.code === 'FORBIDDEN_ROLE');

  const columns: DataTableColumn<AdminSession>[] = [
    {
      key: 'device',
      header: t('admin.sessions.column.device'),
      visibleFrom: 'always',
      cardSlot: 'title',
      grow: true,
      render: (row) => (
        <span className="flex items-center gap-2">
          {row.userAgent || t('admin.sessions.unknownDevice')}
          {row.current ? <Tag tone="brand">{t('admin.sessions.current')}</Tag> : null}
        </span>
      ),
    },
    {
      key: 'ip',
      header: t('admin.sessions.column.ip'),
      visibleFrom: 'md',
      cardSlot: 'line2',
      render: (row) => row.ip ?? t('common.notSet'),
    },
    {
      key: 'lastSeen',
      header: t('admin.sessions.column.lastSeen'),
      visibleFrom: 'always',
      cardSlot: 'line3',
      render: (row) => formatInstantDisplay(row.lastSeenAt, locale),
    },
    {
      key: 'issuedAt',
      header: t('admin.sessions.column.since'),
      visibleFrom: 'lg',
      cardSlot: 'line4',
      render: (row) => formatInstantDisplay(row.issuedAt, locale),
    },
    ...(canRevoke
      ? [
          {
            key: 'actions',
            header: t('admin.sessions.column.actions'),
            visibleFrom: 'always' as const,
            render: (row: AdminSession) => (
              <Button variant="destructive" size="sm" onClick={() => setRevokeOneId(row.id)}>
                {t('admin.sessions.revokeOne')}
              </Button>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t('admin.sessions.title')}
        description={t('admin.sessions.description', { name: userName })}
        breadcrumb={[
          { label: t('admin.users.title'), href: ADMIN_ROUTES.users },
          { label: userName, href: ADMIN_ROUTES.userDetail(userId ?? '') },
          { label: t('admin.sessions.breadcrumb'), href: ADMIN_ROUTES.userSessions(userId ?? '') },
        ]}
        actions={
          canRevoke && rows.length > 0 ? (
            <Button variant="destructive" onClick={() => setRevokeAllOpen(true)}>
              {t('admin.sessions.revokeAll')}
            </Button>
          ) : undefined
        }
      />

      <DataTable
        caption={t('admin.sessions.caption')}
        columns={columns}
        rows={rows}
        getRowId={(row) => row.id}
        loading={sessionsQuery.isLoading}
        error={sessionsQuery.isError}
        onRetry={() => sessionsQuery.refetch()}
        emptyTitle={t('admin.sessions.empty.title')}
        emptyBody={t('admin.sessions.empty.body')}
      />

      <Modal
        open={revokeOneId !== null}
        onOpenChange={(open) => {
          if (!open) setRevokeOneId(null);
        }}
        title={t('admin.sessions.revokeOneConfirm.title')}
        description={t('admin.sessions.revokeOneConfirm.description', { name: userName })}
        confirmLabel={revokeOneMutation.isPending ? t('admin.sessions.revoking') : t('admin.sessions.revokeOne')}
        cancelLabel={t('admin.common.cancel')}
        variant="destructive"
        loading={revokeOneMutation.isPending}
        onConfirm={() => revokeOneId && revokeOneMutation.mutate(revokeOneId)}
      >
        {revokeOneMutation.isError ? (
          <AlertBanner
            variant="critical"
            title={revokeOneMutation.error instanceof NetworkError ? t('admin.error.network') : t('admin.sessions.error.stillActiveOne')}
          />
        ) : null}
      </Modal>

      <Modal
        open={revokeAllOpen}
        onOpenChange={setRevokeAllOpen}
        title={t('admin.sessions.revokeAllConfirm.title')}
        description={t('admin.sessions.revokeAllConfirm.description', { name: userName })}
        confirmLabel={revokeAllMutation.isPending ? t('admin.sessions.revoking') : t('admin.sessions.revokeAll')}
        cancelLabel={t('admin.common.cancel')}
        variant="destructive"
        loading={revokeAllMutation.isPending}
        onConfirm={() => revokeAllMutation.mutate(t('admin.sessions.revokeAllReason'))}
      >
        {revokeAllMutation.isError ? (
          <AlertBanner
            variant="critical"
            title={revokeAllMutation.error instanceof NetworkError ? t('admin.error.network') : t('admin.sessions.error.stillActiveAll')}
          />
        ) : null}
      </Modal>
    </div>
  );
}
